// Core grammar types for Prakrit Suvaani.
// Prakrit has two numbers only (एकवचन, बहुवचन). There is no dual anywhere in this module.

export const PERSONS = ['uttam', 'madhyam', 'anya'] as const
export type Person = typeof PERSONS[number]

export const NUMBERS = ['eka', 'bahu'] as const
export type Num = typeof NUMBERS[number]

export const GENDERS = ['pu', 'stri', 'napum'] as const
export type Gender = typeof GENDERS[number]

export const CASES = [
    'prathama',
    'dvitiya',
    'tritiya',
    'chaturthi',
    'panchami',
    'shashthi',
    'saptami',
    'sambodhan'
] as const
export type Case = typeof CASES[number]

// Pronoun tables carry the first seven cases (no सम्बोधन).
export const PRONOUN_CASES = CASES.slice(0, 7) as Exclude<Case, 'sambodhan'>[]

export const TENSES = [
    'vartaman',
    'bhoot',
    'bhavishya',
    'vidhi',
    'karmani_vartaman',
    'karmani_bhoot',
    'karmani_vidhi',
    'prerak_vartaman',
    'prerak_bhoot',
    'prerak_bhavishya',
    'prerak_vidhi',
    'kriyatipatti'
] as const
export type Tense = typeof TENSES[number]

// The four tenses/moods taught first.
export const BASIC_TENSES: Tense[] = ['vartaman', 'vidhi', 'bhoot', 'bhavishya']

export const LONG_ROOT_TENSES: Tense[] = ['vartaman', 'bhoot', 'bhavishya', 'vidhi', 'kriyatipatti']

export const KRIDANTA_KINDS = [
    'sambandh',
    'hetvarth',
    'vartaman',
    'bhoot',
    'bhavishya',
    'vidhi',
    'karmani_vartaman',
    'karmani_bhavishya',
    'prerak_sambandh',
    'prerak_hetvarth',
    'prerak_vidhi',
    'prerak_vartaman',
    'prerak_bhoot'
] as const
export type KridantaKind = typeof KRIDANTA_KINDS[number]

export type FormStatus = 'core' | 'alt' | 'extrapolated'

export const REVIEW_STATUSES = ['draft', 'needs_review', 'verified', 'published', 'archived'] as const
export type ReviewStatus = typeof REVIEW_STATUSES[number]

export type StemClass = 'a' | 'ā' | 'i' | 'ī' | 'u' | 'ū' | 'e' | 'o'

export type RootClass = 'a' | 'long'

export interface FormEntry {
    form: string
    status: FormStatus
    review_status: ReviewStatus
    rule?: string
    note?: string
}

export type Cell = FormEntry[]

export type PersonTable = Record<Person, Record<Num, Cell>>

export type CaseTable = Record<Case, Record<Num, Cell>>

export type PronounTable = Record<Exclude<Case, 'sambodhan'>, Record<Num, Cell>>

export type KridantaTable = Partial<Record<KridantaKind, Cell>>

export type VerbForms = Partial<Record<Tense, PersonTable>> & {
    kridanta: KridantaTable
}

export interface VerbEntry {
    root: string
    class: RootClass
    hi: string
    en: string
    source: string
    forms: VerbForms
}

export interface NounEntry {
    stem: string
    gender: Gender
    stem_class: StemClass
    hi: string
    en: string
    rules: string
    source: string
    forms: CaseTable
}

export interface AsDhatu {
    root: string
    hi: string
    en: string
    vartaman: PersonTable
    bhoot: PersonTable
    note: string
}

export interface Lang {
    hi: string
    en: string
    pra?: string
}
